import { Link } from "react-router-dom";
import { FileCheck2, Lightbulb, BookMarked, CalendarDays, Sigma, Brain, Target, RotateCcw, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, hasFeature } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Generating, ErrorBox, UpgradeCard } from "@/components/course/States";

const Block = ({ icon: Icon, title, children, className = "" }) => (
  <div className={`card-flat p-6 ${className}`}>
    <p className="eyebrow mb-4 flex items-center gap-1.5"><Icon className="h-4 w-4 text-primary" /> {title}</p>
    {children}
  </div>
);

export const RevisionSheet = ({ r }) => (
  <div className="grid gap-4 md:grid-cols-2" data-testid="revision-sheet">
    <Block icon={Lightbulb} title="Idées essentielles" className="md:col-span-2">
      <ul className="space-y-2 text-sm">{r.essentials?.map((e, i) => <li key={i} className="flex gap-2"><span className="bg-brand mt-2 h-1.5 w-1.5 shrink-0 rounded-full" />{e}</li>)}</ul>
    </Block>
    {r.definitions?.length > 0 && <Block icon={BookMarked} title="Définitions importantes">
      <dl className="space-y-3 text-sm">{r.definitions.map((d, i) => <div key={i}><dt className="font-bold">{d.term}</dt><dd className="text-muted-foreground">{d.definition}</dd></div>)}</dl>
    </Block>}
    {r.dates?.length > 0 && <Block icon={CalendarDays} title="Dates à retenir">
      <ul className="space-y-2 text-sm">{r.dates.map((d, i) => <li key={i}><b>{d.date}</b> — {d.event}</li>)}</ul>
    </Block>}
    {r.formulas?.length > 0 && <Block icon={Sigma} title="Formules utiles">
      <ul className="space-y-2 text-sm">{r.formulas.map((f, i) => <li key={i}><code className="rounded bg-muted px-1.5 py-0.5 font-mono">{f.formula}</code> — {f.description}</li>)}</ul>
    </Block>}
    <Block icon={Brain} title="Résumé à mémoriser" className="bg-accent/40"><p className="text-sm leading-relaxed">{r.memo_summary}</p></Block>
    {r.review_points?.length > 0 && <Block icon={Target} title="Points à revoir">
      <ul className="space-y-2 text-sm">{r.review_points.map((p, i) => <li key={i} className="flex gap-2"><Target className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-500" />{p}</li>)}</ul>
    </Block>}
  </div>
);

export const RevisionTab = ({ c, reload }) => {
  const { user } = useAuth();
  if (!hasFeature(user, "revision")) return <UpgradeCard feature="Les fiches de révision" />;
  const gen = async () => {
    try { await api.post(`/courses/${c.course_id}/revision`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (c.status !== "ready") return <ErrorBox message="Les notes du cours doivent être prêtes avant de générer la fiche." />;
  if (c.revision_status === "processing") return <Generating label="Création de ta fiche de révision…" sub="Environ une minute." />;
  if (!c.revision) return (
    <div className="card-flat flex flex-col items-center px-6 py-16 text-center">
      <FileCheck2 className="h-10 w-10 text-primary" />
      <p className="mt-4 font-semibold">Prêt à réviser ?</p>
      <p className="mt-1 text-sm text-muted-foreground">Idées essentielles, définitions, dates, formules et points à revoir.</p>
      {c.revision_status === "error" && <p className="mt-3 text-sm text-destructive">{c.revision_error}</p>}
      <button data-testid="generate-revision-button" onClick={gen} className="bg-brand mt-6 rounded-full px-6 py-3 text-sm font-semibold text-white">Générer ma fiche de révision</button>
    </div>
  );
  return (
    <div>
      <div className="mb-4 flex flex-wrap justify-end gap-2">
        <button data-testid="regenerate-revision-button" onClick={gen} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold hover:border-primary/40"><RotateCcw className="h-4 w-4" /> Régénérer</button>
        <Link to={`/app/imprimer/${c.course_id}?type=fiche`} target="_blank" data-testid="export-revision-pdf-button" className="bg-brand inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white"><FileDown className="h-4 w-4" /> Exporter en PDF</Link>
      </div>
      <RevisionSheet r={c.revision} />
    </div>
  );
};
